import { z } from "zod";
import { Email, MSG, Password, Token } from "./auth.js";
import { Plan } from "./billing.js";
import { Product } from "./product.js";
import { CompanyOrder, SWITCHER_MSG } from "./switcher.js";
import { AccountName } from "./name.js";

// ---------------------------------------------------------------------------
// ユーザー管理と複数の会社（#77、040、docs/29。射場さん 2026-09-22 の決定）
//   - 人（accounts）と会社の中の人（users）を分ける。1人が何社にでも入れる。会社ごとに料金
//   - 権限は 管理者（admin）／担当者（member）の2つで始める
//   - 利用人数（プランの人数）には 設定待ち・停止中 も数える。除外した利用者は数えない
//   - 「除外」だけ（完全削除は作らない）。名前・実績は残り、同じメールで追加すると同じ利用者として戻る
//   - 文はここに1か所だけ（画面と API が同じ文を出す）
//   - #287（2026-11-01 射場さんの決定 ★1 A、docs/100）：画面の言葉は「招待」をやめて「ユーザー追加」＋「案内メール」、
//     状態は 設定待ち／利用中／停止中、「席」は「利用人数」、「外す」は「除外」。URL（/invite）・API・DB・識別子（invite）は変えない
// ---------------------------------------------------------------------------

export const USER_MSG = {
  familyNameRequired: "姓を入力してください",
  emailRequired: "メールアドレスを入力してください",
  alreadyMember: "この利用者はすでにこの会社に登録されています",
  notFound: "該当する利用者がいません",
  notInvited: "この利用者はすでに利用を開始しています",
  notJoined: "この利用者は設定待ちです。「追加取消」を使ってください",
  lastAdmin: "管理者がこの利用者1人のため変更できません。先に他の利用者を管理者にしてください",
  selfDisable: "自分を停止中にはできません",
  selfRemove: "自分を除外することはできません。他の管理者が操作してください",
  adminOnly: "この操作は管理者のみ行えます",
  roleInvalid: "権限は 管理者 か 担当者 です",
  /**
   * 上限の文（API の 409 PLAN_SEATS_FULL と画面の朱の1行）。人数は呼ぶ側が入れる。
   * 「追加するには、…」からは使い方＝画面は見出しの「？」へ（seatsFullParts。#92 決め2）
   */
  seatsFull: (planLabel: string, limit: number, used: number) =>
    limit === 1
      ? `${planLabel}プランの利用人数は1人です。追加するには、スモール（3人）かビジネス（20人）に変更してください`
      : `${planLabel}プランの利用人数は${limit}人までです（現在 ${used}人。設定待ち・停止中を含む）。追加するには、プランを変更するか、利用していない利用者を除外してください`,
  invited: (name: string) => `${name} さんを追加しました（パスワード設定の案内メールを送信）`,
  /** {project} は画面が t() で埋める（会社の言葉） */
  invitedCameBack: (name: string) => `${name} さんを追加しました（以前の利用者のため、{project}・見積の実績を引き継ぎます）`,
  invitedAgain: (name: string) => `${name} さんは設定待ちです。案内メールを再送しました`,
  resent: (name: string) => `${name} さんに案内メールを再送しました（前のリンクは無効になります）`,
  cancelled: (name: string) => `${name} さんの追加を取り消しました`,
  saved: "保存しました",
  disabled: (name: string) => `${name} さんを停止中にしました（この会社のログインを終了）`,
  /** 除外の知らせ（#287：前の「席が1つ空きました」→ 利用人数をそのまま書く） */
  removed: (name: string, used: number, limit: number) => `${name} さんを除外しました（利用人数 ${used}／${limit}人）`,
  // 案内メールのリンク（/invite。URL・API・識別子の invite は変えない）
  inviteInvalid: "この案内のリンクは無効です",
  inviteExpired: "案内の有効期限（7日間）が切れています。追加者に再送を依頼してください",
  inviteUsed: "この案内は使用済みです。ログインしてください",
  inviteCancelled: "この追加は取り消されています",
  inviteEmailMismatch: "案内メールのアドレスでログインしてください",
  /** {product} は製品名（画面が fillProduct で置き換える。049） */
  inviteLoginRequired: "このメールアドレスは{product}に登録済みです。ログインすると利用を開始できます",
  invitePasswordRequired: "パスワードを設定してください",
  // ログイン・会社を選ぶ
  userDisabled: "この会社では停止中です。管理者に確認してください",
  noCompany: "現在ログインできる会社がありません",
  chooseExpired: MSG.chooseExpired,
  switched: (name: string) => `${name} に切り替えました`,
} as const;

export const UserRole = z.enum(["admin", "member"]);
export type UserRole = z.infer<typeof UserRole>;
export const ROLE_LABEL: Record<UserRole, string> = { admin: "管理者", member: "担当者" };

/** 会社の中の人の状態（DB に列は無く、account_id・disabled_at から決める。外した人は一覧に出ない） */
export const UserStatus = z.enum(["invited", "active", "disabled"]);
export type UserStatus = z.infer<typeof UserStatus>;
/** 状態の名前（#287：招待中／利用できる／利用できない → 設定待ち／利用中／停止中） */
export const STATUS_LABEL: Record<UserStatus, string> = { invited: "設定待ち", active: "利用中", disabled: "停止中" };

export function userStatusOf(u: { accountLinked: boolean; disabledAt: string | null }): UserStatus {
  if (!u.accountLinked) return "invited";
  return u.disabledAt ? "disabled" : "active";
}

export const UserRow = z.object({
  id: z.string().uuid(),
  familyName: z.string(),
  givenName: z.string(),
  /** 「姓 名」 */
  name: z.string(),
  email: z.string(),
  role: UserRole,
  status: UserStatus,
  /** 招待した日時（ISO）。招待中のときに「9/22 に送信」と出す */
  invitedAt: z.string().nullable(),
  joinedAt: z.string().nullable(),
  lastLoginAt: z.string().nullable(),
  disabledAt: z.string().nullable(),
  /** ログイン中の本人 */
  isSelf: z.boolean(),
});
export type UserRow = z.infer<typeof UserRow>;

export const Seats = z.object({
  plan: Plan,
  planLabel: z.string(),
  limit: z.number().int(),
  /** 利用人数（設定待ち・停止中を含む） */
  used: z.number().int(),
  invited: z.number().int(),
  disabled: z.number().int(),
  full: z.boolean(),
});
export type Seats = z.infer<typeof Seats>;

export const UsersPage = z.object({
  users: z.array(UserRow),
  seats: Seats,
});
export type UsersPage = z.infer<typeof UsersPage>;

const PersonName = z.string().trim().max(50, "50文字までです");

export const InviteRequest = z.object({
  familyName: PersonName.min(1, USER_MSG.familyNameRequired),
  givenName: PersonName.default(""),
  email: Email,
  role: UserRole.default("member"),
});
export type InviteRequest = z.infer<typeof InviteRequest>;

/** 省いた欄は変えない */
export const UserUpdate = z.object({
  familyName: PersonName.min(1, USER_MSG.familyNameRequired).optional(),
  givenName: PersonName.optional(),
  role: UserRole.optional(),
  enabled: z.boolean().optional(),
});
export type UserUpdate = z.infer<typeof UserUpdate>;

// ---------------------------------------------------------------------------
// 招待を受ける（/invite?token=）。スマホで開いても読める1枚
// ---------------------------------------------------------------------------

export const InviteState = z.enum(["ok", "expired", "used", "cancelled", "invalid"]);
export type InviteState = z.infer<typeof InviteState>;

export const InviteInfo = z.object({
  state: InviteState,
  companyName: z.string(),
  inviterName: z.string(),
  /** 招待されたメールアドレス（ログイン ID になる） */
  email: z.string(),
  familyName: z.string(),
  givenName: z.string(),
  /** この人はもうアカウント（現場力・営業力で共通）を持っている（ログインすると参加できる） */
  hasAccount: z.boolean(),
  /** いまログインしている人が、招待された本人（「参加」を押すだけ） */
  loggedInAsInvitee: z.boolean(),
  /**
   * いまログインしているのが招待と違うメールのアカウントなら、そのメール（#112、docs/43 §3.5。Apple の非公開メールなど）。
   * 画面は「別のアカウントでログイン中です」の確認を出し、「このまま参加」で confirm=true を送る
   */
  currentEmail: z.string().nullable().default(null),
  /** 招待の権限（#185：参加の「①確認」に出す。20261015_0100 の fn_user_invite_lookup） */
  role: UserRole.default("member"),
});
export type InviteInfo = z.infer<typeof InviteInfo>;

/**
 * 招待を受けて参加する画面の文（#185、docs/74 §3。Figma「招待と会社の追加（10/14）」②）。1画面1つの操作：①確認 → ②パスワード → ③完了。
 *   使っている人（アカウントがある）は ② が「ログイン」、ログイン中の本人は ①確認 → ②完了 の2つ。
 *   #186（docs/74 §9）：業務の言葉に（招待の確認・招待者・氏名・パスワードの設定・参加が完了しました・現在）
 *   #287（docs/100）：「招待」「参加」をやめた（登録内容の確認・追加者・利用開始・ログインして利用開始・利用登録が完了しました）
 */
export const INVITE_JOIN_MSG = {
  stepConfirm: "確認",
  stepPassword: "パスワード",
  stepLogin: "ログイン",
  stepDone: "完了",
  /** 「手順 2／3」の読み上げ */
  stepOf: (n: number, total: number) => `手順 ${n}／${total}`,
  confirmTitle: "登録内容の確認",
  rowCompany: "会社",
  rowInviter: "追加者",
  rowYou: "氏名",
  rowRole: "権限",
  rowLoginId: "ログインID",
  you: (familyName: string, givenName: string) => [familyName, givenName].filter(Boolean).join(" "),
  noCompanyName: "（会社名は未登録）",
  nextPassword: "次へ（パスワード設定）",
  nextLogin: "次へ（ログイン）",
  /** 主ボタン（②のパスワード設定・ログイン中の本人は ①）。#287：「参加」→「利用開始」 */
  join: "利用開始",
  nothing: "心当たりがない場合は、この画面を閉じてください。",
  passwordTitle: "パスワード設定",
  passwordLabel: "パスワード",
  loginTitle: "ログインして利用開始",
  loginHint: (product: string) => `このメールアドレスは${product}に登録済みです。現在のパスワードでログインしてください。`,
  loginJoin: "ログインして利用開始",
  passwordRequired: "パスワードを入力してください",
  doneTitle: "利用登録が完了しました",
  home: "ホームへ",
  pcNote: (host: string) => `パソコンからは ${host} に同じログインIDとパスワードでログインできます。`,
  /** パスワードの欄の右（目のアイコンでなく字。#185 は参加の画面、#186 からログイン・会員登録・再設定・変更も） */
  show: "表示",
  hide: "隠す",
} as const;

export const InviteAcceptRequest = z.object({
  token: Token,
  /** 初めての人だけ（アカウントを作る）。使っている人はログインしてから */
  password: Password.optional(),
  /** 使っている人がその場でログインして参加するとき */
  loginPassword: z.string().min(1).max(200).optional(),
  /** ログイン中の、招待と違うメールのアカウントのまま参加する（#112。確認の「このまま参加」） */
  confirm: z.boolean().optional(),
  remember: z.boolean().default(true),
});
export type InviteAcceptRequest = z.infer<typeof InviteAcceptRequest>;

// ---------------------------------------------------------------------------
// ログイン → 会社を選ぶ／切り替える
// ---------------------------------------------------------------------------

export const CompanyChoice = z.object({
  tenantId: z.string().uuid(),
  name: z.string(),
  role: UserRole,
  /** 自分で登録した会社（よく使う順で記録が無いときに上） */
  own: z.boolean(),
  joinedAt: z.string().nullable(),
  usable: z.boolean(),
  /** usable でない理由：disabled（使えない）／closed（退会の手続き中）／suspended */
  blockedReason: z.enum(["disabled", "closed", "suspended"]).nullable(),
  /** 前回入った会社 */
  last: z.boolean(),
  /** いま入っている会社（ログイン中の切替のとき） */
  current: z.boolean(),
  /** その会社の製品（049）。#151 から画面は製品ごとの節に分ける（いまのドメインの製品が先） */
  product: Product,
  /**
   * いまのドメイン（製品）の会社か。違う製品の会社は false＋reason。#151 からは押すと相手のドメインへ移って入る
   * （POST /auth/handoff/start・/auth/choose/handoff。/auth/switch・/auth/choose は今までどおり 403 PRODUCT_MISMATCH）
   */
  selectable: z.boolean(),
  reason: z.enum(["other_product"]).nullable(),
  /** 会社のロゴ（自社情報）。署名つきの URL（GET /company-logo/:tenantId、翌々日の0時まで）。無ければ null（#151） */
  logoUrl: z.string().nullable().default(null),
  /** 頭文字（companyInitial）。会社名が空なら null＝建物のアイコン（#151） */
  initial: z.string().nullable().default(null),
  /** よく使う順の位置（1 から。使えない会社は下）。「よく使う順」を押したとき画面がその場で並べ直すのに使う（#151） */
  autoRank: z.number().int().default(0),
});
export type CompanyChoice = z.infer<typeof CompanyChoice>;

/** ログインの応答が「会社を選ぶ」のとき */
export const ChooseResponse = z.object({
  choose: z.literal(true),
  chooseToken: Token,
  companies: z.array(CompanyChoice),
  remember: z.boolean(),
  /** ログインした人のメールアドレス（会社を選択の下の「{メール} でログイン中」。パスワード・Google／Apple・パスキー・メールのリンクのどれでも。#151） */
  email: z.string().default(""),
  /** その人の氏名（#152）。会社を追加のシートで「あなたの氏名」を聞くか（needsName）と、入れておく値 */
  account: AccountName.optional(),
});
export type ChooseResponse = z.infer<typeof ChooseResponse>;

export const ChooseRequest = z.object({
  chooseToken: Token,
  tenantId: z.string().uuid(),
  remember: z.boolean().default(true),
});
export type ChooseRequest = z.infer<typeof ChooseRequest>;

export const SwitchRequest = z.object({ tenantId: z.string().uuid() });
export type SwitchRequest = z.infer<typeof SwitchRequest>;

// ---------------------------------------------------------------------------
// 会社の切替（#151、docs/60 §5）
// ---------------------------------------------------------------------------

/** PUT /account/company-order：よく使う順に戻す（auto）／自分で並べる（manual＝全部の会社の並び。使えない会社は省いてよい） */
export const CompanyOrderRequest = z
  .object({ mode: CompanyOrder, tenantIds: z.array(z.string().uuid()).max(200).optional() })
  .refine((v) => v.mode === "auto" || (v.tenantIds?.length ?? 0) > 0, { message: SWITCHER_MSG.orderRequired, path: ["tenantIds"] });
export type CompanyOrderRequest = z.infer<typeof CompanyOrderRequest>;

/** POST /auth/handoff/start（ログイン中）：ほかの製品の会社へ移る鍵 → { url } */
export const HandoffStartRequest = z.object({ tenantId: z.string().uuid() });
export type HandoffStartRequest = z.infer<typeof HandoffStartRequest>;

/** POST /auth/choose/handoff（ログインの続き）：会社を選択の鍵（使用済みにする）で、ほかの製品の会社へ移る鍵 → { url } */
export const ChooseHandoffRequest = z.object({ chooseToken: Token, tenantId: z.string().uuid(), remember: z.boolean().default(true) });
export type ChooseHandoffRequest = z.infer<typeof ChooseHandoffRequest>;

/** 移る先の URL（https://{相手の製品のホスト}/handoff#t=…。鍵は # の後ろ＝サーバーのログ・Referer に残らない） */
export const HandoffStartResponse = z.object({ url: z.string() });
export type HandoffStartResponse = z.infer<typeof HandoffStartResponse>;

/** POST /auth/handoff（相手のドメイン）。confirm＝「入る」のボタンを押した（このドメインにログインが無いとき） */
export const HandoffRequest = z.object({ token: Token, confirm: z.boolean().optional() });
export type HandoffRequest = z.infer<typeof HandoffRequest>;

/** 428 HANDOFF_CONFIRM・409 HANDOFF_OTHER_ACCOUNT の中身（画面は会社のアイコン・会社名・利用者の氏名・ログインID とボタンを出す。#194） */
export const HandoffInfo = z.object({
  company: z.object({ name: z.string(), logoUrl: z.string().nullable(), initial: z.string().nullable() }),
  /** 鍵の持ち主の氏名（accounts の姓・名＝#152。両方空なら null＝画面は「利用者」にメールを出し、「ログインID」の行を出さない） */
  person: z.object({ familyName: z.string(), givenName: z.string() }).nullable().default(null),
  /** 鍵の持ち主のメールアドレス（ログインID） */
  email: z.string(),
  /** 409 のとき：このブラウザでいまログインしている人のメールアドレス */
  currentEmail: z.string().nullable().default(null),
});
export type HandoffInfo = z.infer<typeof HandoffInfo>;

/** 会社を選択の画面で覚えておく「前回の会社」の Cookie */
export const LAST_TENANT_COOKIE = "gb_last_tenant";
/** 招待のリンクの期限（DB の既定 interval '7 days' と合わせる） */
export const INVITE_DAYS = 7;
